import React, { useEffect } from 'react';
import { API_BASE_URL } from '../../config/apiConfig';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { HealthStatusIndicator } from '../../components/HealthStatus';
import VersionDisplay from '../../components/Version/VersionDisplay';

const Login: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/my-assignments';

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  useEffect(() => {
    // Handle Google OAuth callback only
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      login(token);
      navigate(from, { replace: true });
    }
  }, [login, navigate, from]);

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Special case: allow blank password for admin@spectorlab.com
      const isSpecialAdmin = email.toLowerCase() === 'admin@spectorlab.com';
      const loginData = isSpecialAdmin
        ? { email, password: password || '' }
        : { email, password };

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (response.ok) {
        login(data.token);
        toast.success('Login successful!');
        navigate(from, { replace: true });
      } else {
        toast.error(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'black',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
      }}
    >
      <Container component="main" maxWidth={false} disableGutters sx={{ width: '100%' }}>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
          <Box sx={{ width: 'min(560px, 100%)' }}>
            <Card sx={{ width: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography component="h1" variant="h4" gutterBottom>
                    Lab CRM
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Internal Portal & Client Management System
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleLogin}
                  sx={{ mb: 3 }}
                >
                  Sign in with Google
                </Button>

                <Box sx={{ textAlign: 'center', mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography
                    variant="body2"
                    component="button"
                    type="button"
                    onClick={() => navigate('/support')}
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'rgba(255, 140, 0, 0.05)',
                      padding: '8px 16px',
                      borderRadius: 2,
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {
                        background: 'rgba(255, 140, 0, 0.1)'
                      }
                    }}
                  >
                    Need help? View Support & Public Wiki
                  </Typography>

                  <Typography
                    variant="body2"
                    component="button"
                    type="button"
                    onClick={() => setShowForm(!showForm)}
                    sx={{
                      color: 'text.secondary',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      textDecoration: 'underline',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  >
                    {showForm ? 'Use Google instead' : 'Sign in with email'}
                  </Typography>
                </Box>

                {showForm && (
                  <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                      margin="normal"
                      required={email.toLowerCase() !== 'admin@spectorlab.com'}
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      helperText={email.toLowerCase() === 'admin@spectorlab.com' ? 'Password is optional for admin account' : ''}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Sign In'}
                    </Button>
                  </Box>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>For employees:</strong> Use your Google Workspace account to sign in.
                    <br />
                    <strong>For clients:</strong> Use your assigned credentials or Google account.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Health Status Indicator */}
        <HealthStatusIndicator
          showDetails={true}
          position="bottom-center"
          size="medium"
        />

        {/* Version Display */}
        <VersionDisplay
          variant="login"
          showIcon={true}
          showChangelog={false}
        />
      </Container>
    </Box>
  );
};

export default Login;
